import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { PendingReport } from '../../../models/api/reports/all-reports-response';
import { FilterDetailResponse } from '../../../models/api/reports/filter-detail-response';
import { PendingReportDetails } from '../../../models/api/reports/pending-report-details';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { BackButtonComponent } from '../../core/buttons/back-button.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { CardComponent } from '../../core/panels/card.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { HomeCardComponent } from '../../home/home-card.component';
import { FilterViewComponent } from '../core/filter-view.component';
import { ReportFieldViewComponent } from '../core/report-field-view.component';
import { SortFieldViewComponent } from '../core/sort-field-view.component';
import { PendingRequestViewComponent } from './pending-request-view.component';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-pending-report-page',
  imports: [
    RouterLink,
    BackButtonComponent,
    FilterViewComponent,
    CardComponent,
    ActionRowComponent,
    ButtonComponent,
    PageHeaderComponent,
    ReportFieldViewComponent,
    SortFieldViewComponent,
    PendingRequestViewComponent,
    LoadingPanelComponent,
    HomeCardComponent,
  ],
  template: `
    <app-page-header
      [label]="pendingReportsData.outputName"
      labelCase="none"
      labelHeader="Shared report"
      headerSize="h1"
    >
      <ng-template #right>
        <app-action-row>
          <app-button
            right1
            label="Accept"
            icon="fa-light fa-check"
            title="Accept report"
            (click)="acceptReport()"
          />
          <app-button
            right2
            label="Decline"
            emphasis="Low"
            title="Decline report"
            (click)="confirmDecline()"
          /> </app-action-row
      ></ng-template>
    </app-page-header>

    <div class="tw-flex">
      <app-back-button
        left1
        routerLink="/reports"
        [queryParams]="{ tab: 'pending-reports' }"
      >
        Back
      </app-back-button>
    </div>
    <div class="tw-grow tw-justify-center tw-w-full">
      <div class="tw-min-w-0 tw-w-[100%] tw-pt-4 sm:tw-pt-8 tw-mb-4">
        <app-home-card type="Large">
          <div class="tw-min-w-0 tw-w-[100%] tw-p-4 sm:tw-p-8">
            <app-loading-panel
              [state]="loading ? 'Loading' : 'Loaded'"
              loadingStyle="Box"
              [loadingHeight]="500"
              loadingClasses="tw-pb-8"
              [error]="error"
            >
              <ng-template #loaded>
                <div class="tw-pb-8">
                  <app-pending-request-view
                    [pendingReportsData]="pendingReportsData"
                    [pendingReportDetails]="pendingReportsDetails.outputDetails"
                  />
                </div>
                <div
                  class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 sm:tw-gap-8 tw-w-full tw-pb-4 tw-min-w-0"
                >
                  @if (filterDetails) {
                    <app-card
                      label="Filter"
                      class="tw-basis-1/2 tw-min-w-0 tw-overflow-hidden"
                    >
                      <app-filter-view
                        [dataSource]="filterDetails.filterDetail"
                      />
                    </app-card>
                  }
                  <div
                    class="tw-flex tw-gap-4 sm:tw-gap-8 tw-flex-col tw-min-w-0"
                    [class.tw-basis-1/2]="filterDetails"
                    [class.tw-w-full]="!filterDetails"
                  >
                    <app-card
                      label="Report fields"
                      labelCase="titlecase"
                      class="tw-w-full tw-min-w-0"
                    >
                      <app-report-field-view
                        [reportFields]="pendingReportsDetails.reportFields"
                      />
                    </app-card>
                    <app-card
                      label="Sort fields"
                      labelCase="titlecase"
                      class="tw-w-full tw-min-w-0"
                    >
                      <app-sort-field-view
                        [sortBreakFields]="pendingReportsDetails.sortFields"
                      />
                    </app-card>
                  </div>
                </div>
              </ng-template>
            </app-loading-panel>
          </div>
        </app-home-card>
      </div>
    </div>
  `,
  styles: ``,
})
export class PendingReportPageComponent
  extends BaseComponent
  implements OnInit
{
  private snackbar = inject(Snackbar);
  private reportsService = inject(ReportsService);
  protected override router = inject(Router);

  pendingReportsData: PendingReport;

  pendingReportsDetails: PendingReportDetails = {
    outputDetails: [],
    reportFields: [],
    sortFields: [],
  };

  filterDetails: FilterDetailResponse;

  loading = false;
  error: any = null;

  @HandleLoadingState()
  async ngOnInit() {
    // Try to get report from service (normal navigation or sessionStorage restore)
    const report = this.reportsService.currentDetailReport() as PendingReport;

    // If not available, redirect back to reports list
    if (!report) {
      this.router.navigate(['/reports'], { replaceUrl: true });
      return;
    }

    this.pendingReportsData = report;

    const filterId = this.pendingReportsData?.filterId;
    const hasFilter = !!filterId;

    this.pendingReportsDetails = await this.api.reports.pendingReportDetails(
      this.pendingReportsData.requestId,
      'JURIS',
    );

    if (hasFilter) {
      this.filterDetails = await this.api.filters.getFilterDetail(
        filterId,
        this.pendingReportsData?.contract,
      );
      this.filterDetails.filterDetail.filterName =
        this.pendingReportsData.filterName;
    }
  }

  confirmDecline() {
    this.confirm.show({
      label: 'Decline shared report',
      message: `Are you sure you want to decline "${this.pendingReportsData.reportName}"?`,
      width: '35rem',
      data: this.pendingReportsData,
      confirm: (data) => this.declineReport(data),
    });
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async declineReport(report: PendingReport) {
    const response = await this.reportsService.updatePendingReport(
      report.requestId,
      'Delete',
      'JURIS',
    );

    this.snackbar.show(
      'Shared report declined',
      'Success',
      'fa-light fa-file-lines',
      3000,
    );

    this.router.navigate(['/reports'], {
      queryParams: { tab: 'pending-reports' },
    });

    if (response.emails && response.emails.length > 0) {
      const emailText = response.emails.length === 1 ? 'email' : 'emails';
      this.snackbar.show(
        `Notification ${emailText} sent`,
        'Info',
        'fa-light fa-envelope',
        2000,
      );
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async acceptReport() {
    const response = await this.reportsService.updatePendingReport(
      this.pendingReportsData.requestId,
      'Accept',
      'JURIS',
    );

    this.snackbar.show(
      'Shared report accepted',
      'Success',
      'fa-light fa-file-lines',
      3000,
    );

    const hasPendingReports = this.reportsService.pendingReports().length > 0;
    this.router.navigate(['/reports'], {
      queryParams: { tab: hasPendingReports ? 'pending-reports' : 'reports' },
    });

    if (response.emails && response.emails.length > 0) {
      const emailText = response.emails.length === 1 ? 'email' : 'emails';
      this.snackbar.show(
        `Notification ${emailText} sent`,
        'Info',
        'fa-light fa-envelope',
        2000,
      );
    }
  }
}
